import { FC, PropsWithChildren, ReactNode } from 'react';
import { SentinelFeature } from '../../models/organization';
import { useSelector } from 'react-redux';
import { selectUserFeatureFlags } from '../../redux/auth/selectors';

/**
 * A hook to read the current value of a feature flag
 * @param featureName - the name of the feature to read
 * @param defaultValue  - (optional) a default value if the feature does not exist. Defaults to `false`
 * @returns value indicating if the flag is enabled
 */
export function useFeatureFlag(featureName: SentinelFeature): boolean {
  const featureFlags = useSelector(selectUserFeatureFlags);
  return featureFlags[featureName];
}

export type FeatureGateProps = PropsWithChildren<{
  /** the name of the feature */
  featureName: SentinelFeature;
  /** a default value for if the feature does not exist. Defaults to `false` */
  defaultValue?: boolean;
  /** fall back UI to render if the feature is disabled */
  fallback?: ReactNode;
}>;

/**
 * A component that only renders its `children` if the named feature flag is enabled
 */
export const FeatureGate: FC<FeatureGateProps> = ({ featureName, defaultValue, fallback, children }) => {
  const enabled = useFeatureFlag(featureName);
  // eslint-disable-next-line react/jsx-no-useless-fragment
  if (!enabled) return fallback ? null : <>{fallback}</>;
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};
